import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { localStrings } from "../i18n/homeStrings";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft, HardDrive, Download, Trash2, Loader2, FolderKanban,
  CalendarRange, File, FileText, Image as ImageIcon, Archive,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The order sections appear in, and what each is called. Adding a new kind of
// owner means adding a line here and nothing else.
const SECTIONS = [
  { type: "project", labelKey: "files.sectionProjects", icon: FolderKanban, accent: "text-blue-500" },
  { type: "planning", labelKey: "files.sectionPlanning", icon: CalendarRange, accent: "text-[#ff4b33]" },
];

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function iconFor(contentType) {
  if (contentType?.startsWith("image/")) return ImageIcon;
  if (contentType === "application/pdf") return FileText;
  if (contentType?.includes("zip") || contentType?.includes("compressed")) return Archive;
  return File;
}

// Dates follow the language chosen in Core. Passing undefined here hands the
// decision to the operating system, which is how an Italian page ends up
// writing "Aug 27, 2026".
function formatDate(iso, language) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(language === "it" ? "it-IT" : "en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

/**
 * Every file in Core, for an administrator.
 *
 * Two levels: which part of Core a file belongs to, then which project or
 * which year within it. Grouping by owner alone made a page of project names
 * with no indication of what kind of thing they were, so a project and a
 * planning year read as the same sort of heading.
 *
 * Still not a browsable tree. The tree is what the old SharePoint section was,
 * and rebuilding one would bring back the path handling that came with it.
 * These are labels on a flat list, not folders.
 */
export default function Files() {
  const { language } = useLanguage();
  const s = localStrings(language);

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/files/all`, { withCredentials: true });
      setFiles(data || []);
    } catch {
      toast.error(localStrings(language)("files.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (file) => {
    try {
      const res = await axios.get(`${API}/files/${file.id}/download`, {
        withCredentials: true, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error(s("err.fileDownload")); }
  };

  const handleDelete = async (file) => {
    try {
      await axios.delete(`${API}/files/${file.id}`, { withCredentials: true });
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
      toast.success(s("ok.fileDeleted"));
    } catch { toast.error(s("err.fileDelete")); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return files;
    return files.filter(
      (f) =>
        (f.name || "").toLowerCase().includes(q) ||
        (f.owner_label || "").toLowerCase().includes(q)
    );
  }, [files, query]);

  // Section, then owner within it. Order inside each owner is the order the
  // server sent, which is newest first.
  const sections = useMemo(() => {
    return SECTIONS.map((section) => {
      const map = new Map();
      for (const f of filtered) {
        if (f.owner_type !== section.type) continue;
        const key = f.owner_id;
        if (!map.has(key)) {
          map.set(key, {
            key,
            label: f.owner_label,
            closed: f.owner_closed,
            projectId: section.type === "project" ? f.owner_id : null,
            items: [],
          });
        }
        map.get(key).items.push(f);
      }
      const groups = [...map.values()];
      return {
        ...section,
        groups,
        count: groups.reduce((n, g) => n + g.items.length, 0),
        bytes: groups.reduce(
          (n, g) => n + g.items.reduce((m, f) => m + (f.size || 0), 0), 0
        ),
      };
    }).filter((section) => section.groups.length > 0);
  }, [filtered]);

  const totalBytes = useMemo(
    () => files.reduce((sum, f) => sum + (f.size || 0), 0),
    [files]
  );

  return (
    <div className="min-h-screen">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Link to="/" data-testid="back-to-home" className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-700 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-1" />
            {s("files.back")}
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-slate-700 flex items-center justify-center shrink-0">
                <HardDrive className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">{s("files.title")}</h1>
                <p className="text-sm text-slate-500">
                  {s("files.summary", { count: files.length, size: formatSize(totalBytes) })}
                </p>
              </div>
            </div>
            {/* Full width on a phone: capped at 320px it sat short of the edge
                with dead space beside it, which reads as a layout that failed
                rather than as a choice. */}
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={s("files.search")}
              data-testid="files-search"
              className="w-full sm:max-w-xs border-slate-200"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          ) : sections.length === 0 ? (
            <div className="text-center py-20" data-testid="files-empty">
              <HardDrive className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">
                {files.length === 0 ? s("files.empty") : s("files.noMatch")}
              </p>
            </div>
          ) : (
            <div className="space-y-10">
              {sections.map((section) => {
                const SectionIcon = section.icon;
                return (
                  <section key={section.type} data-testid={`files-section-${section.type}`}>
                    {/* What kind of thing these belong to. Without it a project
                        and a planning year were indistinguishable headings. */}
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
                      <SectionIcon className={`w-4 h-4 shrink-0 ${section.accent}`} />
                      <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                        {s(section.labelKey)}
                      </h2>
                      <span className="text-xs text-slate-400 ml-auto shrink-0">
                        {s("files.groupSummary", {
                          count: section.count,
                          size: formatSize(section.bytes),
                        })}
                      </span>
                    </div>

                    <div className="space-y-3">
                      {section.groups.map((group) => {
                        const groupBytes = group.items.reduce((sum, f) => sum + (f.size || 0), 0);
                        return (
                          <div
                            key={group.key}
                            data-testid={`files-group-${section.type}-${group.key}`}
                            className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm"
                          >
                            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50">
                              {group.projectId ? (
                                <Link
                                  to={`/projects/${group.projectId}`}
                                  className="text-sm font-medium text-slate-800 hover:text-indigo-700 truncate"
                                >
                                  {group.label}
                                </Link>
                              ) : (
                                <span className="text-sm font-medium text-slate-800 truncate">{group.label}</span>
                              )}
                              {group.closed && (
                                <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 shrink-0">
                                  {s("files.closed")}
                                </span>
                              )}
                              <span className="text-xs text-slate-400 ml-auto shrink-0">
                                {s("files.groupSummary", {
                                  count: group.items.length,
                                  size: formatSize(groupBytes),
                                })}
                              </span>
                            </div>

                            <div className="divide-y divide-slate-50">
                              {group.items.map((file) => {
                                const Icon = iconFor(file.content_type);
                                return (
                                  <div
                                    key={file.id}
                                    data-testid={`file-row-${file.id}`}
                                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors"
                                  >
                                    <Icon className="w-4 h-4 text-indigo-500 shrink-0" />
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm text-slate-800 truncate">{file.name}</p>
                                      <p className="text-xs text-slate-400 truncate">
                                        {formatSize(file.size)}
                                        {file.uploaded_by_name ? ` \u00b7 ${file.uploaded_by_name}` : ""}
                                        {file.created_at ? ` \u00b7 ${formatDate(file.created_at, language)}` : ""}
                                      </p>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleDownload(file)}
                                      data-testid={`file-download-${file.id}`}
                                      aria-label={s("files.title")}
                                      className="h-8 w-8 text-slate-400 hover:text-indigo-600 shrink-0"
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          data-testid={`file-delete-${file.id}`}
                                          aria-label={s("files.delete")}
                                          className="h-8 w-8 text-slate-400 hover:text-red-600 shrink-0"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>{s("files.deleteTitle")}</AlertDialogTitle>
                                          {/* Deletion here is real: the record
                                              and the object in the bucket both
                                              go. Saying so is the difference
                                              between a confirmation and a
                                              formality. */}
                                          <AlertDialogDescription>
                                            {s("files.deleteDesc", { name: file.name })}
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel className="border-slate-200">
                                            {s("files.cancel")}
                                          </AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={() => handleDelete(file)}
                                            className="bg-red-600 hover:bg-red-700"
                                          >
                                            {s("files.delete")}
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
